<script setup>
import { inject, ref, onMounted, watch, computed } from 'vue';
import {
    Combobox,
    ComboboxInput,
    ComboboxOptions,
    ComboboxOption,
	ComboboxButton,
} from '@headlessui/vue'
import { __, requestSelfcareAccountSubscriber, formatBytes } from '@/utils'
import WhiteBox from '@components/whiteBox/whiteBox.vue';
import PlanInfo from './plan-info.vue';
import UsageInfo from './usage-info.vue';
// import PackageProgress from './package-progress.vue';
import Loader from '@components/loader.vue';
import ProgressCircle from '@components/progress-circle/progress-circle.vue';
import PackageAbroad from '@components/accountMain/parts/package-abroad.vue';

const { details, features } = inject('account');

const searchParams = new URLSearchParams(window.location.search);
const requestedSubscriberId = searchParams.get('subscriber_id');

const subscriberID = (requestedSubscriberId
        ? details.value?.subscribers?.find(sub => sub.id === requestedSubscriberId)
        : null
    ) ?? details.value?.subscribers?.[0];

const currentSubscriber = ref(subscriberID);
const subscriberData = ref({});
const loader = ref(false);

const subscribersArray = computed(() => details.value?.subscribers?.filter(sub => sub.id !== currentSubscriber.value?.id) ?? []);

const getSubscriber = async (id) => {
    loader.value = true;
    let [data, error] = await requestSelfcareAccountSubscriber(id);
    loader.value = false;

    if(error) {
        console.error(error);
    } else {
        if(data) {
            subscriberData.value = data.data;
        }
    }
}

const activationDate = computed(() => {
    const date = new Date(subscriberData.value?.plan_activation_date || '');
    return date.toLocaleDateString("en-GB");
});

watch(currentSubscriber, (value) => {
    getSubscriber(value?.id);
});

onMounted( () => {
    if(currentSubscriber.value?.id) {
        getSubscriber(currentSubscriber.value.id);
    }
});

const currentDate = new Date().toLocaleDateString("en-GB");

const mainPlanServices = computed(() => {
    return subscriberData.value?.services_balances?.reduce((acc, item) => {
        const hasRomaing = item.service_name.toLowerCase().match(/^roaming/);
        const planIncludedService = item.plan_included_service;

        if(!hasRomaing && planIncludedService) {
            acc.push(item);
        } 

        return acc;
    }, []) || [];
});

const servicesItems = computed(() => {
    return subscriberData.value?.services_balances?.reduce((acc, item) => {
        const planIncludedService = item.plan_included_service;
        const hasRoaming = item.service_name.toLowerCase().match(/^roaming/);
        const hasAccumulative = item.service_details?.accumulative;
        const roamingData = item.usage_type === 'roaming_data';

        if (planIncludedService && hasRoaming && !hasAccumulative && roamingData) {
            acc['plan_roaming'] = item;
        } else if (planIncludedService && hasRoaming && hasAccumulative && roamingData) {
            acc['plan_roaming_accumulative'] = {
                ...item,
                group_balance: {
                    ...item.group_balance,
                    usagev: formatBytes(item.group_balance.usagev),
                    total: formatBytes(item.group_balance?.total),
                    left: formatBytes(item.group_balance?.left),
                },
            };
        }

        return acc;
    }, {}) || {};
});

const roamingArray = computed(() => {
    const arr = subscriberData.value?.services_balances?.filter(item => {
        const planIncludedService = item.plan_included_service;
        const hasRoamingUsageType = item.usage_type.toLowerCase().match(/^roaming/);
        const hasRoaming = item.service_name.toLowerCase().match(/^roaming/);
        const hasAccumulative = item.service_details?.accumulative;
        return !planIncludedService && hasRoamingUsageType && hasRoaming && !hasAccumulative;
    }).reduce((acc, item) => {
        if(!acc[item.priority]) {
            acc[item.priority] = {
                data: item,
                start_period: {
                    sec: item.creation_time?.sec || item.from?.sec,
                },
                from: {
                    sec: item.from?.sec,
                },
                to: {
                    sec: item.to?.sec,
                }
           }
           
        }

        switch (item.usage_type) {
            case 'roaming_call':
                acc[item.priority].call = item;
                break;
            case 'roaming_sms':
                acc[item.priority].sms = item;
                break;
            case 'roaming_data':
                acc[item.priority].roaming_data = item;
                break;
        }

        return acc;
    }, {})
    return arr ? Object.values(arr) : [];
});

const boxCount = computed(() => {
    let count = 0;
    const classes = ['', 'one', 'two'];

    if(servicesItems.value.plan_roaming) {
        count++;
    } 
    if(mainPlanServices.value.length) {
        count++;
    } 
    if(servicesItems.value?.plan_roaming_accumulative) {
        count++;
    }
    return `${classes[count]}`;
});

</script>

<template>
    <div class="cellular-phone-wrapper" v-if="details?.subscribers?.length">
        <h5>{{ __('Select a subscription') }}</h5>
        <div class="combobox-outer">
            <Combobox v-model="currentSubscriber">
                <div class="combobox-wrapper">
                    <ComboboxInput :displayValue="(value) => value.name" />
                    <ComboboxButton class="combobox-icon" />
                </div>
                <ComboboxOptions class="combobox-options notice__options">
                    <ComboboxOption
                        v-for="option in subscribersArray"
                        :key="option.id"
                        :value="option"
                        class="combobox-option combobox-option--smaller"
                    >
                        {{ option.name }}
                    </ComboboxOption>
                </ComboboxOptions>
            </Combobox>
        </div>
    </div>

    <h4 v-if="subscriberData.sid">{{ __('my packages') }}</h4>
    <Loader v-if="loader"  color="#2563eb" />
    <WhiteBox v-if="!loader && subscriberData">
        <div class="package-row package-row--main" :class="`package-row--` + boxCount">
            <PlanInfo :data="subscriberData" :main="true">
                <p>
                    {{ __('In a price of') }} <strong>{{ subscriberData?.price }} ₪</strong><br />
                    {{ __('You joined the package on') }} <strong>{{ activationDate }}</strong><br />
                    <template v-if="subscriberData?.days_until_month_end != null">
                        <img :src="`${theme_path.url}/resources/images/svg/icon-circle.svg`" /> {{ __('remained') }} <strong>{{ subscriberData?.days_until_month_end }}</strong> {{ __('A day to reset the package') }}
                    </template>
                </p>

                <button v-if="subscriberData?.plan_activation_date && features.packageChange" class="package-change-btn">
                    <strong>{{ __('Package change') }}</strong> <img :src="`${theme_path.url}/resources/images/svg/icon-arrow-left-double.svg`" />
                </button>
            </PlanInfo>

            <div class="divider" v-if="mainPlanServices?.length"></div>

            <UsageInfo v-if="mainPlanServices?.length" :data="mainPlanServices" />

            <div class="divider" v-if="servicesItems.plan_roaming || servicesItems.plan_roaming_accumulative"></div>
            
            <!-- <PackageProgress :data="subscriberData"/> -->
            
            <div class="package-progress" v-if="servicesItems.plan_roaming">
                <div class="package-progress__item">
                    <h4>{{ __('Use of the surfing package abroad') }}</h4>
                    <ProgressCircle
                        :title="__('eczema')"
                        :current="servicesItems?.plan_roaming.group_balance.usagev" 
                        start="0"
                        :end="servicesItems?.plan_roaming.group_balance.total"
                    />
                </div>
            </div><!-- /.package-progress -->

            <div class="usage-info" v-if="servicesItems.plan_roaming_accumulative">
                <h4 class="usage-info__header">
                    {{ __('Using the overseas surfing package') }}<br/>
                    {{ __('accumulated in the monthly package') }}
                </h4>
                <div class="usage-info__items">
                    <div class="usage-item">
                        <h5>{{ __('You have accumulated') }}</h5>
                        <span class="gradient">{{ servicesItems?.plan_roaming_accumulative.group_balance.total.endValue }}<br /></span>
                        <span class="fz16">GB</span>
                    </div>
                    <div class="divider"></div>
                    <div class="usage-item">
                        <h5>{{ __('You have been roasted.') }}</h5>
                        <span class="gradient">{{ servicesItems?.plan_roaming_accumulative.group_balance.usagev.endValue }}<br /></span>
                        <span class="fz16">GB</span>
                    </div>
                    <div class="divider"></div>
                    <div class="usage-item">
                        <h5>{{ __('You are left with') }}</h5>
                        <span class="gradient">{{ servicesItems?.plan_roaming_accumulative.group_balance.left.endValue }}<br /></span>
                        <span class="fz16">GB</span>
                    </div>
                </div>
                <p class="fz14 mt16 ta-c">
                    <strong>{{ __('As of date') }} {{ currentDate }} {{ __('For surfing abroad') }} <a href="http://xphone.co.il/wp-content/uploads/%D7%A8%D7%A9%D7%99%D7%9E%D7%AA-%D7%99%D7%A2%D7%93%D7%99%D7%9D-%D7%94%D7%9B%D7%9C%D7%95%D7%9C%D7%99%D7%9D-%D7%91%D7%97%D7%91%D7%99%D7%9C%D7%AA-%D7%94%D7%A6%D7%91%D7%99%D7%A8%D7%94-1.pdf" target="_blank" class="gradient">{{ __('To selected destinations') }}</a></strong>
                </p>
            </div>
        </div>
    </WhiteBox>

    <PackageAbroad v-if="roamingArray?.length" :data="roamingArray" />
</template>